/* eslint-disable react/require-default-props */
/* eslint-disable no-alert */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import { useReactToPrint } from 'react-to-print';

import LoadingBidding from 'components/atoms/loading_bidding';
import ItemShare from 'components/atoms/item_share';
import BiddingNoContentError from 'components/atoms/bidding_no_content_error';
import { BIDDING_SUMMARY_ACCORDION_SECTIONS } from 'components/molecules/bidding_summary_accordion_sections';
import {
  biddingSummaryPanelId,
  DEFAULT_SUMMARY_FIELD_EMPTY,
  INITIAL_ACCORDION_OPEN,
  type AccordionKey,
} from 'components/molecules/bidding_summary_constants';
import BiddingSummaryDrawerHeader from 'components/molecules/bidding_summary_drawer_header';
import {
  getDaysUntilIsoDate,
  hasExigenciasEspecificas,
  valorDetalhesTexto,
} from 'components/molecules/bidding_summary_utils';
import BiddingSummaryEditalDownloadButton from 'components/atoms/bidding_summary_edital_download';
import SummaryStatCard from 'components/molecules/summary_stat_card';
import SummaryAccordionBlock from 'components/molecules/summary_accordion_block';

import { ApplicationState } from 'store';
import {
  deleteBidderConsideration,
  fetchBidderConsideration,
  fetchBidderConsiderationTeam,
  fetchBiddingSummary,
  saveBidderConsideration,
} from 'store/modules/bidding_summary_store/actions';
import {
  BiddingSummaryCapagInfo,
  BiddingSummaryEditalDownload,
  BiddingSummaryState,
  BiddingSummaryTypes,
} from 'store/modules/bidding_summary_store/types';
import { BiddingChatTypes } from 'store/modules/bidding_chat/types';
import { MenusBehaviourTypes } from 'store/modules/menus_behaviour_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import { BiddingData, CapagTypes } from 'store/modules/bidding_store/types';

import iconIaGradiente from 'assets/images/icone-ia-gradiente.svg';
import iconValor from 'assets/images/icon_valor.svg';
import iconModalidade from 'assets/images/icon_modalidade.svg';
import iconDataDaSessao from 'assets/images/icon_data_da_sessao.svg';

import {
  biddingSummaryPrintPageStyles,
  BiddingSummaryPrintRoot,
} from 'components/molecules/bidding_summary_print';
import { buildBiddingSummaryShareHtml } from 'components/molecules/bidding_summary_share_email';
import { downloadBiddingSummaryDocx } from 'components/molecules/bidding_summary_docx';

import { formatIsoDateBr, formatIsoDateTimeBr } from 'services/format_iso_date_br';
import createTrack from 'services/actions/tracker_actions';
import tabId from 'services/tabId';

import { useSpeech } from 'hooks/useSpeech';
import { BiddingSummaryDrawerStyles, Container } from './styles';

interface BiddingSummaryProps {
  showModal: boolean;
  biddingId: number;
  isChatOpen?: boolean;
}

const hasEditalFiles = (
  editalDownload?: BiddingSummaryEditalDownload,
): editalDownload is BiddingSummaryEditalDownload => Boolean(editalDownload?.biddingFiles?.length);

const hasVisibleCapag = (
  capagInfo?: BiddingSummaryCapagInfo,
): capagInfo is BiddingSummaryCapagInfo & { capag: CapagTypes } => {
  const capag = capagInfo?.capag;
  return capagInfo?.temCapag === true && Boolean(capag) && capag?.capag !== 'N.D.';
};

const BiddingSummary: React.FC<BiddingSummaryProps> = ({
  showModal,
  biddingId,
  isChatOpen = false,
}) => {
  const [hasNoContent, setHasNoContent] = useState<boolean>(false);
  const [accordionOpen, setAccordionOpen] =
    useState<Record<AccordionKey, boolean>>(INITIAL_ACCORDION_OPEN);
  const [showShareEmail, setShowShareEmail] = useState<boolean>(false);
  const [isGeneratingDocx, setIsGeneratingDocx] = useState<boolean>(false);
  const componentRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const speechPlayback = useSpeech();

  const stopDrawerSpeech = () => {
    speechPlayback.stop();
  };

  const dispatch = useDispatch();

  const { bidding_list, bidding_expired } = useSelector(
    (state: ApplicationState) => state.session.biddings.biddings,
  );

  const resolvedBidding = useMemo((): BiddingData | undefined => {
    const matches = (b: BiddingData) =>
      !!b &&
      typeof b === 'object' &&
      (Number(b.bidding_id) === biddingId || Number(b.id) === biddingId);
    return bidding_list?.find(matches) ?? Object.values(bidding_expired ?? {}).find(matches);
  }, [biddingId, bidding_list, bidding_expired]);

  const { bidding_summary, request } = useSelector<ApplicationState, BiddingSummaryState>(
    (state) => state.session.bidding_summary,
  );

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const current = bidding_summary?.current_bidding_summary?.[tabId()];
  const data = bidding_summary?.bidding_summary_data?.[tabId()];
  const bidderConsideration = bidding_summary?.bidder_consideration_data?.[tabId()];
  const bidderConsiderationTeam = bidding_summary?.bidder_consideration_team_data?.[tabId()] ?? [];
  const currentBiddingId = current?.currentBiddingId;
  const fetchStatus = request.fetch_bidding_summary?.[tabId()];
  const fetchBidderConsiderationStatus = request.fetch_bidder_consideration?.[tabId()];
  const fetchBidderConsiderationTeamStatus = request.fetch_bidder_consideration_team?.[tabId()];
  const saveBidderConsiderationStatus = request.save_bidder_consideration?.[tabId()];
  const deleteBidderConsiderationStatus = request.delete_bidder_consideration?.[tabId()];
  const isLoadingBiddingSummary = Boolean(fetchStatus?.loading);
  const fallbackEditalDownload = useMemo(() => {
    if (!resolvedBidding) return undefined;

    return {
      biddingStatusEdital: resolvedBidding.edital_status_id,
      biddingFiles: resolvedBidding.edicts,
      situation: resolvedBidding?.bidding_grouping?.descricao,
      biddingValidade: resolvedBidding?.data_validade,
    };
  }, [resolvedBidding]);
  const editalDownload =
    currentBiddingId === biddingId
      ? [current?.editalDownload, fallbackEditalDownload].find(hasEditalFiles)
      : undefined;
  const fallbackCapagInfo = useMemo(() => {
    if (!resolvedBidding) return undefined;

    return {
      temCapag: resolvedBidding.public_body?.tem_capag,
      capag: resolvedBidding.public_body?.capag,
    };
  }, [resolvedBidding]);
  const capag =
    currentBiddingId === biddingId
      ? [current?.capagInfo, fallbackCapagInfo].find(hasVisibleCapag)?.capag
      : undefined;

  const defaultEmptyValue = DEFAULT_SUMMARY_FIELD_EMPTY;

  const summary = data?.summary;

  const openFeedBackModal = (pathname = 'Resumo do Edital') => {
    stopDrawerSpeech();
    dispatch({ type: BiddingSummaryTypes.CLOSE_BIDDING_SUMMARY });
    dispatch({
      type: MenusBehaviourTypes.OPEN_FEEDBACK_MODAL,
      pathname,
    });
  };

  const handleEmailChecklistClick = () => {
    stopDrawerSpeech();
    setShowShareEmail((open) => {
      if (!open) {
        createTrack('Resumo do Edital - Enviar checklist por e-mail');
      }
      return !open;
    });
  };

  const toggleAccordion = (key: AccordionKey) => {
    setAccordionOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleBiddingChatClick = () => {
    stopDrawerSpeech();

    if (isChatOpen) {
      dispatch({ type: BiddingChatTypes.CLOSE_CHAT });
      return;
    }

    dispatch({ type: BiddingChatTypes.OPEN_CHAT, biddingId });
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Resumo do Edital - Conlicitação ${currentBiddingId}`,
    pageStyle: biddingSummaryPrintPageStyles,
  });

  const handleGenerateDocx = async () => {
    if (isGeneratingDocx) return;

    stopDrawerSpeech();
    setIsGeneratingDocx(true);

    const numeroLicitacao = summary?.numero_licitacao?.trim();
    const filenameBase = numeroLicitacao
      ? `Resumo do Edital ${numeroLicitacao} - Conlicitação ${biddingId}`
      : `Resumo do Edital - Conlicitação ${biddingId}`;

    try {
      const generated = await downloadBiddingSummaryDocx(componentRef.current, filenameBase);
      if (generated) {
        createTrack('Resumo do Edital - Gerou checklist docx');
      } else {
        createTrack('Resumo do Edital - Falha ao gerar checklist docx');
        window.alert('Não foi possível gerar o documento. Tente novamente.');
      }
    } catch {
      createTrack('Resumo do Edital - Falha ao gerar checklist docx');
      window.alert('Não foi possível gerar o documento. Tente novamente.');
    } finally {
      setIsGeneratingDocx(false);
    }
  };

  const handleSaveBidderConsideration = (consideration: string) => {
    dispatch(saveBidderConsideration(biddingId, consideration));
    createTrack('Resumo do Edital - Salvou considerações do licitante');
  };

  const handleDeleteBidderConsideration = () => {
    dispatch(deleteBidderConsideration(biddingId));
    createTrack('Resumo do Edital - Excluiu considerações do licitante');
  };

  const closeAndClearSummary = () => {
    stopDrawerSpeech();
    setShowShareEmail(false);
    dispatch({ type: BiddingSummaryTypes.CLOSE_BIDDING_SUMMARY });
    dispatch({ type: BiddingSummaryTypes.CLEAR_BIDDING_SUMMARY_DATA });
  };

  useEffect(() => {
    if (!showModal) {
      speechPlayback.stop();
      setShowShareEmail(false);
    }
  }, [showModal, speechPlayback.stop]);

  const accordionSections = useMemo(
    () =>
      BIDDING_SUMMARY_ACCORDION_SECTIONS.filter(
        (section) =>
          (section.key !== 'seguroGarantia' || Boolean(summary?.garantias)) &&
          (section.key !== 'exigenciasEspecificas' ||
            (profile?.alfa_tester === true &&
              hasExigenciasEspecificas(summary?.exigencias_especificas))) &&
          (section.key !== 'questionamentos' ||
            !(
              Array.isArray(summary?.possibilidades_questionamentos) &&
              summary.possibilidades_questionamentos.length === 0
            )),
      ),
    [
      profile?.alfa_tester,
      summary?.garantias,
      summary?.exigencias_especificas,
      summary?.possibilidades_questionamentos,
    ],
  );

  useEffect(() => {
    if (biddingId === 0 || !showModal) return;

    dispatch(fetchBiddingSummary(biddingId));
    dispatch(fetchBidderConsideration(biddingId));
    dispatch(fetchBidderConsiderationTeam(biddingId));
  }, [dispatch, showModal, biddingId]);

  useEffect(() => {
    if (!showShareEmail) return;
    setAccordionOpen(INITIAL_ACCORDION_OPEN);
  }, [showShareEmail]);

  useEffect(() => {
    const isEmpty = !data?.summary;

    if (!isLoadingBiddingSummary && showModal && biddingId === currentBiddingId && isEmpty) {
      setHasNoContent(true);
    } else {
      setHasNoContent(false);
    }
  }, [data, isLoadingBiddingSummary, showModal, biddingId, currentBiddingId]);

  const isLegacyGarantiaFormat = Boolean(summary?.garantia?.trim());
  const bidderConsiderationText = (bidderConsideration?.consideration || '').trim();
  const hasTeamConsiderations = bidderConsiderationTeam.length > 0;

  const bidderConsiderationErrorMessage = useMemo(() => {
    if (saveBidderConsiderationStatus?.error) {
      return 'Não foi possível salvar suas considerações. Tente novamente.';
    }
    if (deleteBidderConsiderationStatus?.error) {
      return 'Não foi possível excluir suas considerações. Tente novamente.';
    }
    if (fetchBidderConsiderationStatus?.error) {
      return 'Não foi possível carregar suas considerações. Tente novamente.';
    }
    if (fetchBidderConsiderationTeamStatus?.error) {
      return 'Não foi possível carregar as considerações da equipe. Tente novamente.';
    }
    return undefined;
  }, [
    saveBidderConsiderationStatus?.error,
    deleteBidderConsiderationStatus?.error,
    fetchBidderConsiderationStatus?.error,
    fetchBidderConsiderationTeamStatus?.error,
  ]);

  const accordionCtx = summary
    ? {
        summary,
        biddingId,
        bidding: resolvedBidding,
        defaultEmptyValue,
        isLegacyGarantiaFormat,
        capag,
        bidderConsideration: bidderConsideration?.consideration || '',
        bidderConsiderationTeam,
        isLoadingBidderConsideration: Boolean(fetchBidderConsiderationStatus?.loading),
        isSavingBidderConsideration: Boolean(saveBidderConsiderationStatus?.loading),
        isDeletingBidderConsideration: Boolean(deleteBidderConsiderationStatus?.loading),
        bidderConsiderationErrorMessage,
        onSaveBidderConsideration: handleSaveBidderConsideration,
        onDeleteBidderConsideration: handleDeleteBidderConsideration,
      }
    : null;

  return (
    <>
      <BiddingSummaryDrawerStyles />
      <Modal
        show={showModal}
        onHide={closeAndClearSummary}
        backdrop={!isChatOpen}
        centered={false}
        enforceFocus={false}
        className={`bidding-summary-drawer-modal ${
          isChatOpen ? 'bidding-summary-drawer-modal--with-chat' : ''
        }`}
        dialogClassName="bidding-summary-drawer-dialog"
        contentClassName="bidding-summary-drawer-content"
        aria-labelledby="bidding-summary-drawer-title"
      >
        <Container className="bidding-summary-container">
          <BiddingSummaryDrawerHeader
            titleId="bidding-summary-drawer-title"
            title={
              <>
                Resumo do Edital
                {summary?.numero_licitacao?.trim() ? ` ${summary.numero_licitacao.trim()}` : ''}
              </>
            }
            subtitle={<>Nº Conlicitação: {biddingId}</>}
            getSpeechText={() => componentRef.current?.textContent || ''}
            onClose={closeAndClearSummary}
            onChatClick={() => {
              handleBiddingChatClick();
              createTrack(
                isChatOpen
                  ? 'Resumo do Edital - Fechou Pergunte ao Edital (cabecalho)'
                  : 'Resumo do Edital - Pergunte ao Edital (cabecalho)',
              );
            }}
            isChatOpen={isChatOpen}
            iaIconSrc={iconIaGradiente}
            speechPlayback={speechPlayback}
          />
          <div className="modal-body p-0 d-flex flex-column flex-grow-1 bidding-summary-drawer-body">
            <div className="bidding-summary flex-grow-1">
              {isLoadingBiddingSummary && (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="position-relative">
                    <LoadingBidding
                      title="Carregando resumo do edital."
                      subtitle="Aguarde enquanto buscamos as informações."
                    />
                  </div>
                </div>
              )}

              {!isLoadingBiddingSummary && hasNoContent && (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="position-relative">
                    <BiddingNoContentError
                      title="Não foi possível carregar o resumo do edital desta licitação."
                      subtitle="Tente novamente mais tarde."
                    />
                  </div>
                </div>
              )}

              {!isLoadingBiddingSummary && !hasNoContent && summary && accordionCtx && (
                <div className="bidding-summary-inner bg-white">
                  <BiddingSummaryPrintRoot
                    ref={componentRef}
                    biddingId={biddingId}
                    numeroLicitacao={summary?.numero_licitacao}
                  >
                    <div className="row mx-n2 summary-stat-row">
                      <SummaryStatCard
                        iconSrc={iconValor}
                        label="Valor Estimado"
                        value={valorDetalhesTexto(summary)}
                      />
                      <SummaryStatCard
                        iconSrc={iconModalidade}
                        label="Modalidade"
                        value={
                          summary.tipo_contratacao?.trim() || summary.modo_disputa?.trim() || '—'
                        }
                        badge={
                          summary.tipo_contratacao?.trim() &&
                          summary.modo_disputa?.trim() &&
                          summary.tipo_contratacao.trim() !== summary.modo_disputa.trim() ? (
                            <span className="badge badge-modo-disputa mt-2">
                              {summary.modo_disputa}
                            </span>
                          ) : undefined
                        }
                      />
                      <SummaryStatCard
                        iconSrc={iconDataDaSessao}
                        label="Data da Sessão"
                        value={formatIsoDateBr(summary.data_sessao_publica) || '—'}
                        badge={(() => {
                          const dias = getDaysUntilIsoDate(summary.data_sessao_publica);
                          if (dias === null || dias < 0) return undefined;
                          return (
                            <span className="badge badge-sessao-dias mt-2">
                              Em {dias} {dias === 1 ? 'dia' : 'dias'}
                            </span>
                          );
                        })()}
                      />
                    </div>

                    <div className="mt-3">
                      {accordionSections.map((section) => {
                        if (section.key === 'exigenciasEspecificas' && accordionCtx) {
                          return (
                            <React.Fragment key={section.key}>
                              {section.render(accordionCtx)}
                            </React.Fragment>
                          );
                        }

                        const iconProps = section.iconSrc
                          ? { iconSrc: section.iconSrc }
                          : { icon: section.icon || 'article' };
                        const omitConsideracoesFromShare =
                          section.key === 'consideracoesLicitante' &&
                          !bidderConsiderationText &&
                          !hasTeamConsiderations;
                        const mergedCardClass = [
                          section.cardClassName,
                          omitConsideracoesFromShare
                            ? 'd-print-none bidding-summary-omit-share-empty'
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' ');

                        return (
                          <SummaryAccordionBlock
                            key={section.key}
                            open={accordionOpen[section.key]}
                            onToggle={() => toggleAccordion(section.key)}
                            accordionPanelId={biddingSummaryPanelId(section.key)}
                            title={section.title}
                            cardClassName={mergedCardClass || undefined}
                            headerClassName={section.headerClassName}
                            {...iconProps}
                          >
                            {section.render(accordionCtx)}
                          </SummaryAccordionBlock>
                        );
                      })}
                    </div>

                    <div className="d-flex flex-wrap align-items-center justify-content-between px-3 py-2 text-muted summary-field-label summary-print-feedback border-top mt-2 d-print-none">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-left mr-3 mb-1"
                        onClick={() => openFeedBackModal()}
                      >
                        Deixe seu feedback sobre esta ferramenta
                      </button>
                      {summary.resumo_criado_em?.trim() ? (
                        <span className="small mb-1 ml-auto text-right">
                          Resumo gerado em {formatIsoDateTimeBr(summary.resumo_criado_em)}
                        </span>
                      ) : null}
                    </div>
                  </BiddingSummaryPrintRoot>
                </div>
              )}
            </div>
            {!isLoadingBiddingSummary && !hasNoContent && summary && (
              <div className="bidding-summary-footer d-flex flex-column flex-shrink-0 px-3 py-3">
                <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center">
                  <div className="mr-md-auto mb-2 mb-md-0 d-flex flex-wrap align-items-center">
                    <BiddingSummaryEditalDownloadButton
                      key={`bidding-summary-edital-${biddingId}`}
                      editalDownload={editalDownload}
                    />
                  </div>
                  <div className="d-flex flex-wrap justify-content-end">
                    <div className="bidding-summary-email-anchor mr-2 mb-1 mb-md-0">
                      {showShareEmail && (
                        <div className="bidding-summary-email-flyout">
                          <ItemShare
                            shareBoxLayout="inline"
                            setShowShareWindow={setShowShareEmail}
                            selectedItems={[biddingId]}
                            getShareHtml={() => buildBiddingSummaryShareHtml(componentRef.current)}
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={handleEmailChecklistClick}
                      >
                        Enviar Checklist por E-mail
                      </button>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary mr-2 mb-1 mb-md-0"
                      onClick={handleGenerateDocx}
                      disabled={isGeneratingDocx}
                    >
                      {isGeneratingDocx ? 'Gerando .docx...' : 'Gerar Checklist .docx'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary mb-1 mb-md-0"
                      onClick={() => {
                        handlePrint();
                        createTrack('Resumo do Edital - Clicou em imprimir');
                      }}
                    >
                      Imprimir Checklist
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </Modal>
    </>
  );
};

export default BiddingSummary;
